// PLAN-cockpit-v2-domain-lenses Phase 3: Health Lens
// Sub-sidebar: 直近 7 日ログ一覧 + 日付選択
// Main: Health Daily Log 入力フォーム + health entries タイムライン
// v1 HealthDashboardCard から移植、v2 デザイントークン適用

import { useEffect, useState } from 'react'
import { listOperationalData, upsertOperationalData, type HealthDailyLog } from '../../api/kdb'
import { apiFetch } from '../../api/client'
import { ResultRow } from '../components/common/ResultRow'
import { EmptyState } from '../components/common/EmptyState'
import { useSelectedItem } from '../hooks/useSelectedItem'
import { useViewport } from '../hooks/useViewport'
import type { SearchHit, SearchResponse } from '../../types'

const DATASET = 'health_daily_log'

function todayISO(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// ===== Sub-sidebar =====

export function HealthSidebar() {
  const [logs, setLogs] = useState<HealthDailyLog[]>([])
  const [reload, setReload] = useState(0)

  useEffect(() => {
    let cancelled = false
    listOperationalData<HealthDailyLog>(DATASET, { limit: 7 })
      .then((res) => { if (!cancelled) setLogs(res.rows) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [reload])

  return (
    <div className="p-4 space-y-4 text-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-xs uppercase tracking-wide text-text-subtle">Health Log</h2>
        <button type="button" onClick={() => setReload(n => n + 1)}
          className="text-xs text-text-subtle hover:text-text-base">↺</button>
      </div>
      <div className="space-y-1">
        {logs.length === 0 && <p className="text-xs text-text-muted">記録なし</p>}
        {logs.map((row) => (
          <div key={row.log_date} className="text-xs border-b border-border-subtle pb-1">
            <span className="font-mono text-text-subtle">{row.log_date}</span>
            <div className="text-text-muted mt-0.5">
              {row.sleep_hours != null && <span className="mr-2">😴 {row.sleep_hours}h</span>}
              {row.steps != null && <span className="mr-2">👣 {row.steps.toLocaleString()}</span>}
              {row.weight_kg != null && <span>⚖️ {row.weight_kg}kg</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ===== Main Lens =====

export function HealthLens() {
  const { setSelectedItem, selectedItem } = useSelectedItem()
  const viewport = useViewport()
  const [reload, setReload] = useState(0)
  const [showDetails, setShowDetails] = useState(false)

  // form state
  const [logDate, setLogDate] = useState(todayISO())
  const [sleepHours, setSleepHours] = useState('')
  const [sleepQuality, setSleepQuality] = useState('')
  const [steps, setSteps] = useState('')
  const [exerciseMinutes, setExerciseMinutes] = useState('')
  const [exerciseType, setExerciseType] = useState('')
  const [weightKg, setWeightKg] = useState('')
  const [restingHr, setRestingHr] = useState('')
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitMsg, setSubmitMsg] = useState<string | null>(null)

  // timeline
  const [timeline, setTimeline] = useState<SearchHit[]>([])
  const [tlLoading, setTlLoading] = useState(true)

  // recent daily logs (Operational Data)
  const [recentLogs, setRecentLogs] = useState<HealthDailyLog[]>([])
  const [showAllLogs, setShowAllLogs] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    setTlLoading(true)
    Promise.all([
      apiFetch<SearchResponse>('/api/search', {
        query: { domain: 'health', domain_mode: 'broad', sort: '-created_at', limit: 50 },
        signal: ac.signal,
      }).catch(() => null),
      listOperationalData<HealthDailyLog>(DATASET, { limit: 14 }).catch(() => null),
    ]).then(([tl, logs]) => {
      setTimeline(tl?.entries ?? [])
      setRecentLogs(logs?.rows ?? [])
      setTlLoading(false)
    })
    return () => ac.abort()
  }, [reload])

  function fillFromLog(row: HealthDailyLog) {
    setLogDate(row.log_date)
    setSleepHours(row.sleep_hours?.toString() ?? '')
    setSleepQuality(row.sleep_quality?.toString() ?? '')
    setSteps(row.steps?.toString() ?? '')
    setExerciseMinutes(row.exercise_minutes?.toString() ?? '')
    setExerciseType(row.exercise_type ?? '')
    setWeightKg(row.weight_kg?.toString() ?? '')
    setRestingHr(row.resting_hr?.toString() ?? '')
    setNotes(row.notes ?? '')
    if (viewport === 'mobile') {
      setShowDetails(true)
      window.scrollTo(0, 0)
    }
  }

  function resetForm() {
    setLogDate(todayISO()); setSleepHours(''); setSleepQuality('')
    setSteps(''); setExerciseMinutes(''); setExerciseType('')
    setWeightKg(''); setRestingHr(''); setNotes(''); setSubmitMsg(null)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true); setSubmitMsg(null)
    try {
      const body: Record<string, unknown> = { log_date: logDate, source: 'cockpit' }
      if (sleepHours) body.sleep_hours = parseFloat(sleepHours)
      if (sleepQuality) body.sleep_quality = parseInt(sleepQuality, 10)
      if (steps) body.steps = parseInt(steps, 10)
      if (exerciseMinutes) body.exercise_minutes = parseInt(exerciseMinutes, 10)
      if (exerciseType) body.exercise_type = exerciseType
      if (weightKg) body.weight_kg = parseFloat(weightKg)
      if (restingHr) body.resting_hr = parseInt(restingHr, 10)
      if (notes) body.notes = notes
      await upsertOperationalData<HealthDailyLog>(DATASET, body)
      setSubmitMsg(`保存しました (${logDate})`)
      setReload(n => n + 1)
    } catch (e: unknown) {
      setSubmitMsg(`保存失敗: ${e instanceof Error ? e.message : String(e)}`)
    } finally { setSubmitting(false) }
  }

  const inp = 'px-2 py-1.5 bg-bg-base border border-border-subtle rounded text-sm text-text-base placeholder:text-text-subtle focus:outline-none focus:border-border-strong'

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-3xl mx-auto px-8 py-10 space-y-10">
        <header>
          <p className="text-xs uppercase tracking-wide text-text-subtle">Health</p>
          <h1 className="text-2xl font-medium text-text-base mt-1">Daily Log</h1>
        </header>

        {/* 入力フォーム */}
        <section>
          <h2 className="text-xs uppercase tracking-wide text-text-subtle mb-4">Record</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Primary fields: 常に表示 */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-xs text-text-subtle">日付 *</span>
                <input type="date" value={logDate} onChange={e => setLogDate(e.target.value)} required className={inp} />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-text-subtle">睡眠 (h)</span>
                <input type="number" step="0.1" value={sleepHours} onChange={e => setSleepHours(e.target.value)} placeholder="7.5" className={inp} />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-text-subtle">歩数</span>
                <input type="number" value={steps} onChange={e => setSteps(e.target.value)} placeholder="8000" className={inp} />
              </label>
            </div>

            {/* モバイル: 詳細展開ボタン */}
            {viewport === 'mobile' && (
              <button type="button" onClick={() => setShowDetails(s => !s)}
                className="text-sm text-accent-primary hover:underline text-left">
                {showDetails ? '▲ 詳細を閉じる' : '▼ 詳細を入力 (睡眠の質・運動・体重・心拍)'}
              </button>
            )}

            {/* Secondary fields: デスクトップは常に表示、モバイルは展開時のみ */}
            {(viewport !== 'mobile' || showDetails) && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-text-subtle">睡眠の質 (1-10)</span>
                  <input type="number" min="1" max="10" value={sleepQuality} onChange={e => setSleepQuality(e.target.value)} placeholder="7" className={inp} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-text-subtle">運動 (分)</span>
                  <input type="number" value={exerciseMinutes} onChange={e => setExerciseMinutes(e.target.value)} placeholder="30" className={inp} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-text-subtle">運動種別</span>
                  <input type="text" value={exerciseType} onChange={e => setExerciseType(e.target.value)} placeholder="walk,gym" className={inp} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-text-subtle">体重 (kg)</span>
                  <input type="number" step="0.1" value={weightKg} onChange={e => setWeightKg(e.target.value)} placeholder="65.0" className={inp} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-text-subtle">安静時心拍 (bpm)</span>
                  <input type="number" value={restingHr} onChange={e => setRestingHr(e.target.value)} placeholder="58" className={inp} />
                </label>
              </div>
            )}

            <label className="flex flex-col gap-1">
              <span className="text-xs text-text-subtle">notes (主観メモ)</span>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                placeholder="疲労感あり / 寝付き悪い 等"
                className={`${inp} resize-none`} />
            </label>
            <div className="flex items-center gap-3">
              <button type="submit" disabled={submitting}
                className="px-4 py-1.5 bg-accent-primary text-bg-surface rounded text-sm hover:opacity-90 disabled:opacity-60">
                {submitting ? '保存中…' : '保存'}
              </button>
              <button type="button" onClick={resetForm} disabled={submitting}
                className="px-4 py-1.5 border border-border-subtle rounded text-sm text-text-muted hover:bg-bg-overlay">
                クリア
              </button>
              {submitMsg && <span className="text-sm text-text-subtle">{submitMsg}</span>}
            </div>
          </form>
        </section>

        {/* Recent Daily Logs (Operational Data) */}
        <section>
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-xs uppercase tracking-wide text-text-subtle">Recent Logs</h2>
            {recentLogs.length > 3 && (
              <button type="button" onClick={() => setShowAllLogs(s => !s)}
                className="text-xs text-accent-primary hover:underline">
                {showAllLogs ? '▲ 閉じる' : `▼ すべて見る (${recentLogs.length})`}
              </button>
            )}
          </div>
          {recentLogs.length === 0 ? (
            <p className="text-sm text-text-muted">まだログがありません</p>
          ) : (
            <ul className="space-y-1.5">
              {(showAllLogs ? recentLogs : recentLogs.slice(0, 3)).map(row => (
                <li key={row.log_date}>
                  <button type="button" onClick={() => fillFromLog(row)}
                    className="w-full text-left px-3 py-2 rounded border border-border-subtle hover:bg-bg-overlay transition">
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <span className="font-mono text-text-base">{row.log_date}</span>
                      <div className="flex items-center gap-2 text-text-muted text-[11px]">
                        {row.sleep_hours != null && <span>😴 {row.sleep_hours}h</span>}
                        {row.steps != null && <span>👣 {row.steps.toLocaleString()}</span>}
                        {row.weight_kg != null && <span>⚖ {row.weight_kg}kg</span>}
                      </div>
                    </div>
                    {row.notes && <p className="text-[11px] text-text-muted line-clamp-1 mt-0.5">{row.notes}</p>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* health entries タイムライン */}
        <section>
          <h2 className="text-xs uppercase tracking-wide text-text-subtle mb-4">Health Entries</h2>
          {tlLoading ? (
            <p className="text-sm text-text-subtle">Loading…</p>
          ) : timeline.length === 0 ? (
            <EmptyState title="No health entries" description="health domain の Entry がありません" />
          ) : (
            <ul className="border-t border-border-subtle">
              {timeline.map(hit => {
                const itemType: 'entry' | 'article' = hit.origin === 'synthesized' ? 'article' : 'entry'
                return (
                  <li key={hit.id}>
                    <ResultRow
                      id={hit.id}
                      title={hit.title}
                      one_line={hit.one_line}
                      origin={hit.origin}
                      subjectivity={hit.subjectivity}
                      source_project={hit.source_project ?? null}
                      domains={hit.domains ?? []}
                      visibility={hit.visibility ?? null}
                      has_decision={hit.has_decision}
                      x_status={hit.x_status ?? null}
                      created_at={hit.created_at}
                      selected={selectedItem?.id === hit.id}
                      onClick={() => setSelectedItem({ id: hit.id, itemType })}
                    />
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  )
}
